import React, { ReactNode } from 'react';

import { classNames } from '@/helpers/classNames';
import { useTeam } from '@/providers/TeamProvider/TeamProvider';
import {
	CircularProgress,
	Avatar as MuiAvatar,
	SvgIcon,
} from '@material-ui/core';
import { isNil } from 'ramda';

import { S3ImageWrapper } from '../S3ImageWrapper/S3ImageWrapper';
import styles from './AsaphAvatar.module.scss';

type S3Object = any;

export type AvatarSize =
	| 'xx-small'
	| 'x-small'
	| 'small'
	| 'medium'
	| 'large'
	| 'x-large'
	| 'xx-large'
	| 'xxx-large';

export type AsaphAvatarBackgroundTypes =
	| 'default'
	| 'yellow'
	| 'green'
	| 'teal'
	| 'aqua'
	| 'purple'
	| 'maroon'
	| 'red'
	| 'orange'
	| 'blue';

export interface AvatarProps {
	userId?: string;
	size: AvatarSize;
	s3Image?: S3Object;
	src?: string;
	name: string;
	icon?: React.ElementType;
	hasBorder?: boolean;
	isRectangular?: boolean;
	background?: AsaphAvatarBackgroundTypes;
	progress?: number;
	additionalProgress?: number;
	className?: string;
	hasNotification?: boolean;
	notificationSize?: 'small' | 'medium';
	isActive?: boolean;
	activeSize?: 'small' | 'medium';
}

const getStyles = (size: AvatarSize) => {
	switch (size) {
		case 'xx-small':
			return {
				avatar: styles['avatar__wrapper--xx-small'],
				text: [
					styles['avatar__text'],
					styles['avatar__text--xx-small'],
				],
			};
		case 'x-small':
			return {
				avatar: styles['avatar__wrapper--x-small'],
				text: [styles['avatar__text'], styles['avatar__text--x-small']],
			};
		case 'small':
			return {
				avatar: styles['avatar__wrapper--small'],
				text: [styles['avatar__text'], styles['avatar__text--small']],
			};
		case 'medium':
			return {
				avatar: styles['avatar__wrapper--medium'],
				text: [styles['avatar__text'], styles['avatar__text--medium']],
			};
		case 'large':
			return {
				avatar: styles['avatar__wrapper--large'],
				text: [styles['avatar__text'], styles['avatar__text--large']],
			};
		case 'x-large':
			return {
				avatar: styles['avatar__wrapper--x-large'],
				text: [styles['avatar__text'], styles['avatar__text--x-large']],
			};
		case 'xx-large':
			return {
				avatar: styles['avatar__wrapper--xx-large'],
				text: [
					styles['avatar__text'],
					styles['avatar__text--xx-large'],
				],
			};
		case 'xxx-large':
			return {
				avatar: styles['avatar__wrapper--xxx-large'],
				text: [
					styles['avatar__text'],
					styles['avatar__text--xxx-large'],
				],
			};
		default:
			throw new Error('Unknown size provided');
	}
};

const getProgressSizes = (size: AvatarSize) => {
	switch (size) {
		case 'xx-small':
			return {
				size: 33,
				thickness: 1,
			};
		case 'x-small':
			return {
				size: 46,
				thickness: 1.5,
			};
		case 'small':
			return {
				size: 58,
				thickness: 1.5,
			};
		case 'medium':
			return {
				size: 74,
				thickness: 2,
			};
		case 'large':
			return {
				size: 91,
				thickness: 2,
			};
		case 'x-large':
			return {
				size: 122,
				thickness: 2,
			};
		case 'xx-large':
			return {
				size: 158,
				thickness: 2,
			};
		case 'xxx-large':
			return {
				size: 182,
				thickness: 2,
			};
		default:
			throw new Error('Unknown size provided');
	}
};

export const AsaphAvatar = ({
	name,
	icon,
	size,
	src,
	s3Image,
	hasBorder,
	isRectangular,
	background = 'default',
	progress,
	additionalProgress,
	className,
	hasNotification,
	notificationSize = 'small',
	isActive,
	activeSize = 'small',
}: AvatarProps) => {
	const {
		team: { members },
	} = useTeam();
	const classes = getStyles(size);
	const progressSizes = getProgressSizes(size);
	const nameInitialsOrIcon = icon ? (
		<SvgIcon fontSize="inherit" component={icon} />
	) : (
		name[0]
	);
	const avatarWrapper = (component: ReactNode, imageSrc?: string) => {
		const avatarRootClasses = classNames([
			styles['avatar__root'],
			hasBorder && styles['avatar__root--border'],
			isRectangular && styles['avatar__root--rectangular'],
			s3Image && styles['avatar__root--no-background'],
		]);
		return (
			<div className={classNames([classes.avatar, className])}>
				<MuiAvatar
					alt={name}
					src={imageSrc}
					classes={{
						root: avatarRootClasses,
						colorDefault: styles[`avatar__color--${background}`],
					}}
				>
					<div className={classes.text.join(' ')}>{component}</div>
				</MuiAvatar>
				{hasNotification && (
					<div
						className={classNames([
							styles['avatar__notification'],
							styles[`size-${notificationSize}`],
						])}
					/>
				)}
				{isActive && (
					<div
						className={classNames([
							styles['avatar__active'],
							styles[`size-${activeSize}`],
						])}
					/>
				)}
				{!isNil(progress) && (
					<>
						{!isNil(additionalProgress) && (
							<div
								className={`${styles['avatar__progress']} ${styles['avatar__progress--additional']}`}
							>
								<CircularProgress
									variant="determinate"
									value={additionalProgress}
									color="secondary"
									size={progressSizes.size}
									thickness={progressSizes.thickness}
								/>
							</div>
						)}
						<div className={styles['avatar__progress']}>
							<CircularProgress
								variant="determinate"
								value={progress}
								size={progressSizes.size}
								thickness={progressSizes.thickness}
							/>
						</div>
					</>
				)}
			</div>
		);
	};

	return s3Image != null
		? avatarWrapper(
				<S3ImageWrapper
					className="todo"
					// imgKey={s3Image.key'}
					// level={s3Image.visibility}
					// identityId={s3Image.uploaderId}
					// // Since we cannot changed underline <img> we have to reset fontSize as it causes overflow due to display:inline
					// style={{ fontSize: 0 }}
				/>,
		  )
		: avatarWrapper(nameInitialsOrIcon, src);
};

export default AsaphAvatar;
